
<template>
    <div class="my-orders">
        <!-- Loading -->
        <div v-if="loading" class="mo-state">
            <v-progress-circular indeterminate :color="primaryColor" size="32" width="2.5" />
            <div class="mo-state-title mt-3">{{ $t('conta.carregandoPedidos') }}</div>
        </div>

        <!-- Vazio -->
        <div v-else-if="!dados.length" class="mo-state">
            <div class="mo-empty-icon" :style="{ background: tintColor }">
                <v-icon size="34" :color="primaryColor">mdi-receipt-text-outline</v-icon>
            </div>
            <div class="mo-state-title mt-3">{{ $t('conta.nenhumPedido') }}</div>
            <div class="mo-state-sub">{{ $t('conta.pedidosUltimos6Meses') }}</div>
        </div>

        <!-- Lista -->
        <div v-else>
            <div class="mo-count">
                {{ $t(dados.length > 1 ? 'conta.contagemPedidos' : 'conta.contagemPedido', { n: dados.length }) }}
            </div>

            <div class="mo-list">
                <div
                    v-for="item in dados"
                    :key="item.id"
                    class="mo-card"
                    @click="abrirAcompanhamento(item)"
                >
                    <div class="mo-card-top">
                        <span class="mo-id">{{ $t('conta.pedidoN', { id: item.id }) }}</span>
                        <span class="mo-total">{{ formatMoney(+item.total) }}</span>
                    </div>

                    <div class="mo-card-bottom">
                        <div class="mo-meta">
                            <v-icon size="13" class="mo-meta-icon">mdi-clock-outline</v-icon>
                            <span>{{ formatDate(item.created_at, $t('conta.formatoDataHora')) }}</span>
                            <span class="mo-dot">·</span>
                            <span class="mo-payment">{{ getFormaPagamento(item.forma_pagamento) }}</span>
                        </div>

                        <component
                            :is="isAguardando(item) ? 'a' : 'span'"
                            :href="isAguardando(item)
                                ? `/${$route.params.emp}/pagamento/${item.uuid}`
                                : undefined"
                            :class="['mo-status', `mo-status--${statusType(item.status)}`]"
                            @click.stop
                        >
                            {{ statusLabel(item.status) }}
                            <v-icon
                                v-if="isAguardando(item)"
                                size="11"
                                class="ml-1"
                            >mdi-open-in-new</v-icon>
                        </component>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { mapState } from 'vuex';

export default {
    name: 'MyOrders',

    data: () => ({
        loading: false,
        dados: [],
    }),

    computed: {
        ...mapState([
            'cliente',
            'configuracoes',
        ]),

        primaryColor() {
            return (this.$theme && this.$theme.primary)
                || (this.$vuetify.theme.themes.light && this.$vuetify.theme.themes.light.primary)
                || '#d32f2f';
        },

        tintColor() {
            const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(this.primaryColor || '');
            if (!m) {
                return 'rgba(0, 0, 0, 0.06)';
            }
            return `rgba(${parseInt(m[1], 16)}, ${parseInt(m[2], 16)}, ${parseInt(m[3], 16)}, 0.12)`;
        },
    },

    mounted() {
        this.consultar();
    },

    methods: {
        isAguardando(item) {
            return item.status === 'aguardando_pagamento';
        },

        abrirAcompanhamento(item) {
            this.$router.push(`/${this.$route.params.emp}/acompanhe-seu-pedido/${item.uuid}`).catch(() => {});
        },

        getFormaPagamento(formaPagamentoId) {
            const formas = (this.configuracoes && this.configuracoes.formas_pagamento) || [];
            const forma = formas.find(f => f.id == formaPagamentoId);
            return (forma && this.tSistema('sistema.formaPagamento.' + forma.id, forma.descricao)) || '—';
        },

        statusLabel(status) {
            return this.$te('conta.statusPedido.' + status)
                ? this.$t('conta.statusPedido.' + status)
                : (status || '—');
        },

        statusType(status) {
            const types = {
                aguardando_pagamento: 'warning',
                pago: 'success',
                recebido: 'neutral',
                cancelado: 'danger',
                confirmado: 'info',
                em_preparacao: 'info',
                saiu_para_entrega: 'info',
                aguardando_retirada: 'warning',
                concluido: 'success',
            };
            return types[status] || 'neutral';
        },

        consultar() {
            this.dados = [];

            if (!this.cliente || !this.cliente.id) {
                return;
            }

            this.loading = true;
            this.$http.get('account/pedidos').then(resp => {
                this.dados = resp.data.data;
            }).catch(() => {
                this.notify(this.$t('status.naoConcluiuAviso'), 'warning');
            }).finally(() => (this.loading = false));
        },
    },
}
</script>

<style scoped>
/* ===== Estados (loading / vazio) ===== */
.mo-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 48px 24px;
    text-align: center;
}

.mo-empty-icon {
    width: 68px;
    height: 68px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
}

.mo-state-title {
    font-size: 0.96rem;
    color: #555;
    font-weight: 700;
}

.mo-state-sub {
    font-size: 0.78rem;
    color: #aaa;
    margin-top: 4px;
    max-width: 230px;
    line-height: 1.4;
}

/* ===== Lista ===== */
.mo-count {
    padding: 14px 16px 8px;
    font-size: 0.72rem;
    color: #b0b0b0;
    text-transform: uppercase;
    letter-spacing: 0.4px;
    font-weight: 600;
}

.mo-list {
    padding: 0 12px 14px;
    display: flex;
    flex-direction: column;
    gap: 10px;
}

.mo-card {
    border: 1px solid #eee;
    border-radius: 12px;
    padding: 12px 14px;
    background: #fff;
    cursor: pointer;
    transition: box-shadow 0.15s ease, border-color 0.15s ease;
}

.mo-card:hover {
    box-shadow: 0 3px 12px rgba(0, 0, 0, 0.06);
    border-color: #e2e2e2;
}

.mo-card-top {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
}

.mo-id {
    font-weight: 700;
    font-size: 0.9rem;
    color: #333;
}

.mo-total {
    font-weight: 700;
    font-size: 0.95rem;
    color: #222;
    flex-shrink: 0;
}

.mo-card-bottom {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    margin-top: 9px;
}

.mo-meta {
    display: flex;
    align-items: center;
    gap: 5px;
    font-size: 0.74rem;
    color: #9e9e9e;
    min-width: 0;
}

.mo-meta-icon {
    color: #c4c4c4 !important;
}

.mo-dot {
    color: #ddd;
}

.mo-payment {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    min-width: 0;
}

/* ===== Badge de status (pílula) ===== */
.mo-status {
    display: inline-flex;
    align-items: center;
    font-size: 0.7rem;
    font-weight: 700;
    padding: 4px 11px;
    border-radius: 20px;
    white-space: nowrap;
    text-decoration: none;
    flex-shrink: 0;
}

.mo-status--success { color: #2e7d32; background: #e8f5e9; }
.mo-status--warning { color: #e65100; background: #fff3e0; }
.mo-status--danger  { color: #c62828; background: #ffebee; }
.mo-status--info    { color: #1565c0; background: #e3f2fd; }
.mo-status--neutral { color: #616161; background: #f5f5f5; }

/* Aguardando pagamento é clicável -> destaca como ação */
a.mo-status {
    box-shadow: inset 0 0 0 1px rgba(230, 81, 0, 0.25);
}

a.mo-status:hover {
    filter: brightness(0.97);
}
</style>
